import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useTranslation } from "../i18n";
import "./Landing.css";

export default function Landing() {
  const { t } = useTranslation();
  const timelineRef = useRef(null);
  const videoRef = useRef(null);
  const videoSectionRef = useRef(null);
  const videoScrollRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("timeline-visible");
          }
        });
      },
      { threshold: 0.2 }
    );

    const steps = timelineRef.current?.querySelectorAll(".timeline-step");
    steps?.forEach((step) => {
      step.classList.add("timeline-animate");
      observer.observe(step);
    });

    const demoSection = document.querySelector(".demo-video-section");
    if (demoSection) {
      demoSection.classList.add("timeline-animate");
      observer.observe(demoSection);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const videoEl = videoRef.current;
    const scrollContainer = videoScrollRef.current;
    if (!videoEl || !scrollContainer) return;

    const onScroll = () => {
      const rect = scrollContainer.getBoundingClientRect();
      const scrollHeight = rect.height - window.innerHeight;
      const progress = Math.min(1, Math.max(0, -rect.top / scrollHeight));
      if (videoEl.duration) {
        videoEl.currentTime = progress * videoEl.duration;
      }
      if (progressRef.current) {
        progressRef.current.style.width = `${progress * 100}%`;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const timelineSteps = [
    { label: t("landing.step0Label"), title: t("landing.step0Title"), desc: t("landing.step0Desc"), icon: "📡", hint: t("landing.step0Hint"), image: "/images/step-0-configure.png",
      planTiers: [
        { name: t("landing.planFree"), channels: t("landing.planFreeChannels") },
        { name: t("landing.planStarter"), channels: t("landing.planStarterChannels") },
        { name: t("landing.planPro"), channels: t("landing.planProChannels") },
      ],
    },
    { label: t("landing.step1Label"), title: t("landing.step1Title"), desc: t("landing.step1Desc"), icon: "🔗", hint: t("landing.step1Hint"), image: "/images/step-1-connect.png",
      integrations: [
        { name: "Notion", active: true },
        { name: "Obsidian", active: false },
        { name: "Todoist", active: false },
      ],
    },
  ];

  const testimonials = [
    { tag: t("landing.testimonial1Tag"), tagClass: "topic-geo", quote: t("landing.testimonial1Quote"), role: t("landing.testimonial1Role"), city: t("landing.testimonial1City") },
    { tag: t("landing.testimonial2Tag"), tagClass: "topic-tech", quote: t("landing.testimonial2Quote"), role: t("landing.testimonial2Role"), city: t("landing.testimonial2City") },
    { tag: t("landing.testimonial3Tag"), tagClass: "topic-fin", quote: t("landing.testimonial3Quote"), role: t("landing.testimonial3Role"), city: t("landing.testimonial3City") },
  ];

  return (
    <div className="landing">
      {/* Hero */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-gradient" />
          <div className="hero-grid" />
        </div>

        <div className="hero-content">
          <div className="hero-eyebrow">
            <span className="hero-eyebrow-dot" />
            {t("landing.eyebrow")}
          </div>
          <h1 className="hero-title">
            {t("landing.heroTitle1")}<br />
            <span className="hero-title-accent">{t("landing.heroTitle2")}</span>
          </h1>
          <p className="hero-subtitle">{t("landing.heroSubtitle")}</p>
          <div className="hero-actions">
            <Link to="/pricing" className="btn-primary btn-lg">
              {t("landing.ctaStart")}
              <span className="btn-arrow">→</span>
            </Link>
            <a href="#how-it-works" className="btn-ghost btn-lg">
              {t("landing.ctaHow")}
            </a>
          </div>
          <div className="hero-value-props">
            <div className="value-prop">
              <span className="value-prop-icon">📡</span>
              <span className="value-prop-text">{t("landing.valueProp1")}</span>
            </div>
            <div className="value-prop value-prop-accent">
              <span className="value-prop-icon">💬</span>
              <span className="value-prop-text">{t("landing.valueProp2")}</span>
            </div>
            <div className="value-prop">
              <span className="value-prop-icon">📝</span>
              <span className="value-prop-text">{t("landing.valueProp3")}</span>
            </div>
          </div>
        </div>

        {/* Proof — Brent oil price graph */}
        <div className="hero-preview">
          <div className="proof-card">
            <div className="proof-content">
              <div className="proof-message">
                <div className="proof-label">
                  <span className="proof-label-dot" />
                  {t("landing.proofLabel")}
                </div>
                <div className="proof-question">
                  "{t("landing.proofQuestion")} <span className="proof-highlight">{t("landing.proofBefore")}</span> {t("landing.proofAfter")}"
                </div>
                <div className="proof-context">{t("landing.proofContext")}</div>
                <div className="proof-badge">
                  <span className="proof-badge-pill">{t("landing.proofBadge")}</span>
                  <span className="proof-badge-time">{t("landing.proofBadgeTime")}</span>
                </div>
              </div>
              <div className="proof-divider" />
              <div className="proof-graph">
                <div className="proof-chart">
                  <span className="proof-chart-label proof-chart-label--top">$78</span>
                  <span className="proof-chart-label proof-chart-label--bottom">$66</span>
                  <div className="proof-chart-grid" />
                  <svg viewBox="0 0 300 160" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="spikeGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f87171" />
                        <stop offset="100%" stopColor="transparent" />
                      </linearGradient>
                    </defs>
                    <polyline points="0,110 15,114 30,108 45,112 60,104 75,110 90,118 105,113 120,120 135,115 150,118 165,122 180,118 195,125 210,120 220,122" fill="none" stroke="#60a5fa" strokeWidth="2" opacity="0.7" />
                    <polyline points="220,122 225,118 230,105 235,72 240,42 245,30 250,24 255,34 260,26 265,36 270,18 275,28 280,14 285,24 290,18 295,22 300,16" fill="none" stroke="#f87171" strokeWidth="2.5" />
                    <polygon points="220,122 225,118 230,105 235,72 240,42 245,30 250,24 255,34 260,26 265,36 270,18 275,28 280,14 285,24 290,18 295,22 300,16 300,160 220,160" fill="url(#spikeGrad)" opacity="0.15" />
                  </svg>
                  <div className="proof-marker">
                    <span className="proof-marker-label">Feb 27</span>
                  </div>
                </div>
                <div className="proof-axis-x">
                  <span>Jun</span><span>Oct</span><span>2026</span><span>Apr</span>
                </div>
                <div className="proof-source">{t("landing.proofSource")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section" id="how-it-works">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-eyebrow">{t("landing.timelineEyebrow")}</span>
            <h2 className="section-title">
              {t("landing.timelineTitle1")}<br />
              <em>{t("landing.timelineTitle2")}</em>
            </h2>
            <p className="section-desc">{t("landing.timelineDesc")}</p>
          </div>

          <div className="timeline" ref={timelineRef}>
            {timelineSteps.map((step, i) => (
              <div
                key={step.label}
                className={`timeline-step ${i % 2 === 0 ? "step-left" : "step-right"}`}
              >
                <div className="timeline-screenshot">
                  {step.image ? (
                    <div className="screenshot-placeholder">
                      <img src={step.image} alt={step.hint} />
                    </div>
                  ) : (
                    <div className="screenshot-placeholder">
                      <span className="screenshot-icon">{step.icon}</span>
                      <span className="screenshot-label">{t("landing.screenshot")}</span>
                      <span className="screenshot-hint">{step.hint}</span>
                    </div>
                  )}
                </div>
                <div className="timeline-content">
                  <div className="step-indicator">
                    <div className="step-num">{i + 1}</div>
                    <span className="step-label">{step.label}</span>
                  </div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-desc">{step.desc}</p>
                  {step.planTiers && (
                    <div className="step-plan-tiers">
                      {step.planTiers.map((tier) => (
                        <div key={tier.name} className="plan-tier">
                          <span className="tier-name">{tier.name}</span>
                          <span className="tier-channels">{tier.channels}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {step.integrations && (
                    <div className="step-integrations">
                      {step.integrations.map((int) => (
                        <span
                          key={int.name}
                          className={`integration-tag ${int.active ? "active" : ""}`}
                        >
                          {int.name}{!int.active && ` (${t("landing.soon")})`}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {i < timelineSteps.length - 1 && (
                  <div className="timeline-connector" />
                )}
              </div>
            ))}
          </div>

          {/* Timeline connector to video */}
          <div className="timeline-connector" />

          {/* Demo video — tall scroll container with sticky inner */}
          <div className="demo-video-scroll-container" ref={videoScrollRef}>
            <div className="demo-video-section" ref={videoSectionRef}>
              <div className="demo-video-header">
                <div className="step-indicator">
                  <div className="step-num">3</div>
                  <span className="step-label">{t("landing.demoLabel")}</span>
                </div>
                <h3 className="step-title">{t("landing.demoTitle")}</h3>
                <p className="step-desc">{t("landing.demoDesc")}</p>
              </div>
              <div className="demo-video-container">
                <video
                  ref={videoRef}
                  src="/videos/how-it-works-demo.webm"
                  preload="auto"
                  muted
                  playsInline
                />
              </div>
              <div className="demo-progress-track">
                <div className="demo-progress" ref={progressRef} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section section-dark">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-eyebrow">{t("landing.testimonialsEyebrow")}</span>
            <h2 className="section-title">{t("landing.testimonialsTitle")}<br /><em>{t("landing.testimonialsTitle2")}</em></h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((tm) => (
              <div key={tm.tag} className="testimonial-card">
                <div className="testimonial-tag">
                  <span className={`topic-tag ${tm.tagClass}`}>{tm.tag}</span>
                </div>
                <div className="testimonial-quote">"{tm.quote}"</div>
                <div className="testimonial-author">
                  <div className="testimonial-role">{tm.role}</div>
                  <div className="testimonial-city">{tm.city}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-inner">
          <div className="cta-glow" />
          <h2 className="cta-title">
            {t("landing.ctaTitle1")}<br />
            <em>{t("landing.ctaTitle2")}</em>
          </h2>
          <p className="cta-desc">{t("landing.ctaDesc")}</p>
          <Link to="/pricing" className="btn-primary btn-xl">
            {t("landing.ctaButton")}
            <span className="btn-arrow">→</span>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <img src="/logos/logo-name-green.png" alt="ViewsDay" className="footer-logo" />
          </div>
          <div className="footer-links">
            <Link to="/pricing">{t("nav.pricing")}</Link>
            <Link to="/login">{t("nav.signIn")}</Link>
            <Link to="/contact">{t("contact.title")}</Link>
            <Link to="/privacy">Política de Privacidade</Link>
            <Link to="/terms">Termos de Serviço</Link>
          </div>
          <div className="footer-copy">
            {t("landing.footerRights", { year: new Date().getFullYear() })}
          </div>
        </div>
      </footer>
    </div>
  );
}
